import React, { useState, useEffect } from 'react';
import {
  Drawer, Form, Input, Select, Button, InputNumber,
  TimePicker, Row, Col, List, Divider, Popover, message, Radio, Upload, Typography, Image, Alert, Tabs, DatePicker
} from 'antd';
import { MessageOutlined, FileOutlined } from '@ant-design/icons';
import TimeSelector from './TimeSelector';
import TargetSelector from './TargetSelector';
import TagEditor from './TagEditor';
import AttachmentManager from './AttachmentManager';
import useMediaState from '../../hooks/useMediaState';
import useInputBoxes from '../../hooks/useInputBoxes';
import useStepFormLogic from '../../hooks/useStepFormLogic';
import useFileUploadLogic from '../../hooks/useFileUploadLogic';
import useStepFormInit from '../../hooks/useStepFormInit';
import {
  SOP_CONSTANTS,
  stepTypes,
  emojiData
} from '../../constants/sopConstants';
import dayjs from 'dayjs';
import zhCN from 'antd/lib/locale/zh_CN';
import 'dayjs/locale/zh-cn';
import { generateUniqueStepCode } from '../../utils/sopUtils';
import EmployeeSelect from '../common/EmployeeSelect';

dayjs.locale('zh-cn');

const { Option } = Select;
const { Text } = Typography;

// URL验证正则表达式
const URL_REGEX = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?(\?\S*)?$/i;

// URL验证函数
const validateUrl = (_, value) => {
  if (!value) {
    return Promise.reject('请输入URL');
  }

  if (!URL_REGEX.test(value)) {
    return Promise.reject('请输入有效的URL（例如: https://example.com）');
  }
  return Promise.resolve();
};

const StepModal = ({
  open: visible,
  title,
  currentStep,
  form,
  messageList = [],
  previewUrls,
  uploading,
  uploadProgress,
  onSave,
  onCancel,
  handleFileUpload,
  handleRemoveFile,
  isTemplateActive = false,
  tags,
  handleUpdatePreviewUrls,
  isScheduled = false,
  groups = []
}) => {
  // 使用自定义Hook管理状态
  const {
    mediaState,
    setMediaState,
    updateMediaId,
    updateSpecificMediaId,
    updateOssUrl,
    updateImageGroup,
    clearMedia,
    removeFromImageGroup
  } = useMediaState();

  const {
    inputBoxes,
    momentBoxes,
    setInputBoxes,
    setMomentBoxes,
    initializeInputBoxes,
    updateInputBoxContent,
    updateInputBoxType,
    addInputBox,
    removeInputBox,
    restoreFromMessageList,
    restoreFromMomentsList
  } = useInputBoxes();

  // 使用新的Hooks
  const { buildStepData } = useStepFormLogic();
  const { initFormData: initializeFormData } = useStepFormInit();

  // 先定义内容更新函数，因为useFileUploadLogic需要使用它们
  const handleUpdateInputBoxContent = (id, fieldName, value) => {
    // 使用函数式更新避免状态丢失
    let boxType;
    setInputBoxes(prevBoxes => {
      const currentBox = prevBoxes.find(box => box.id === id);
      boxType = currentBox?.type;
      
      return prevBoxes.map(box =>
        box.id === id ? { ...box, [fieldName]: value } : box
      );
    });

    if (fieldName === 'mediaId' || fieldName === 'videoMediaId' || fieldName === 'fileMediaId') {
      setMediaState(prev => {
        const newState = { ...prev };

        newState.messageMediaIds = {
          ...prev.messageMediaIds,
          [id]: value
        };

        if (fieldName === 'mediaId' && boxType === 'IMAGE') {
          newState.messageImageIds = {
            ...prev.messageImageIds,
            [id]: value
          };
        } else if (fieldName === 'videoMediaId' || (fieldName === 'mediaId' && boxType === 'VIDEO')) {
          newState.messageVideoIds = {
            ...prev.messageVideoIds,
            [id]: value
          };
        } else if (fieldName === 'fileMediaId' || (fieldName === 'mediaId' && boxType === 'FILE')) {
          newState.messageFileIds = {
            ...prev.messageFileIds,
            [id]: value
          };
        }

        return newState;
      });
    }
    
    // 处理ossUrl字段
    if (fieldName === 'ossUrl' || fieldName === 'videoMediaId_ossUrl' || fieldName === 'mediaId_ossUrl') {
      setMediaState(prev => {
        return {
          ...prev,
          messageOssUrls: {
            ...prev.messageOssUrls,
            [id]: value
          }
        };
      });
    }
    
    if (fieldName === 'imageGroup' && boxType === 'IMAGE_GROUP') {
      setMediaState(prev => {
        return {
          ...prev,
          messageImageGroupIds: {
            ...prev.messageImageGroupIds,
            [id]: value
          }
        };
      });
    }
  };

  const handleUpdateMomentBoxContent = (id, fieldName, value) => {
    console.log(`更新朋友圈内容 - ID: ${id}, 字段: ${fieldName}, 值:`, value);

    // 使用函数式更新避免状态丢失
    setMomentBoxes(prevBoxes => {
      const currentBox = prevBoxes.find(box => box.id === id);
      const boxType = currentBox?.type;
      
      return prevBoxes.map(box =>
        box.id === id ? { ...box, [fieldName]: value } : box
      );
    });

    // 获取当前box类型用于后续逻辑
    const currentBox = momentBoxes.find(box => box.id === id);
    const boxType = currentBox?.type;

    if (fieldName === 'mediaId') {
      setMediaState(prev => {
        const newState = { ...prev };

        newState.momentMediaIds = {
          ...prev.momentMediaIds,
          [id]: value
        };

        if (boxType === 'MOMENT_IMAGE') {
          newState.momentImageIds = {
            ...prev.momentImageIds,
            [id]: value
          };
        } else if (boxType === 'MOMENT_VIDEO') {
          newState.momentVideoIds = {
            ...prev.momentVideoIds,
            [id]: value
          };
        }

        return newState;
      });
    }
    
    // 处理ossUrl字段
    if (fieldName === 'ossUrl' || fieldName === 'mediaId_ossUrl') {
      setMediaState(prev => {
        return {
          ...prev,
          momentOssUrls: {
            ...prev.momentOssUrls,
            [id]: value
          }
        };
      });
    }
    
    if (fieldName === 'imageGroup' && boxType === 'MOMENT_IMAGE_GROUP') {
      setMediaState(prev => {
        return {
          ...prev,
          momentImageGroupIds: {
            ...prev.momentImageGroupIds,
            [id]: value
          }
        };
      });
    }
  };

  // 使用文件上传Hook
  const { handleFileUploadWrapper, handleRemoveFileWrapper } = useFileUploadLogic(
    handleFileUpload,
    handleRemoveFile,
    handleUpdatePreviewUrls,
    mediaState,
    setMediaState,
    updateMediaId,
    updateSpecificMediaId,
    updateOssUrl,
    updateImageGroup,
    handleUpdateInputBoxContent,
    handleUpdateMomentBoxContent,
    inputBoxes,
    momentBoxes,
    form
  );

  // 组件挂载或currentStep变化时初始化数据
  useEffect(() => {
    if (visible) {
      initializeFormData(
        currentStep,
        isScheduled,
        form,
        restoreFromMessageList,
        restoreFromMomentsList,
        initializeInputBoxes,
        handleUpdatePreviewUrls
      );
    }
  }, [visible, currentStep]);

  // 监听inputBoxes变化，恢复消息媒体状态
  useEffect(() => {
    if (inputBoxes && inputBoxes.length > 0) {
      inputBoxes.forEach(box => {
        if (box.type === 'IMAGE_GROUP' && box.imageGroup) {
          console.log(`恢复消息图片集: ${box.id}`, box.imageGroup);
          updateImageGroup(box.id, box.imageGroup, 'MESSAGE');
          
          if (box.imageGroup.mediaIds && typeof handleUpdatePreviewUrls === 'function') {
            box.imageGroup.mediaIds.forEach(mediaId => {
              const ossUrl = box.imageGroup.ossUrls[mediaId];
              if (ossUrl) {
                handleUpdatePreviewUrls({ [mediaId]: ossUrl });
                console.log(`消息图片集预览URL: ${mediaId} -> ${ossUrl}`);
              }
            });
          }
        } else if (['IMAGE', 'VIDEO', 'FILE'].includes(box.type) && box.mediaId) {
          updateMediaId(box.id, box.mediaId, 'MESSAGE');
          
          const mediaType = box.type.toLowerCase();
          updateSpecificMediaId(box.id, box.mediaId, mediaType, 'MESSAGE');
          
          if (box.ossUrl) {
            updateOssUrl(box.id, box.ossUrl, 'MESSAGE');
            
            if (typeof handleUpdatePreviewUrls === 'function') {
              handleUpdatePreviewUrls({ [box.mediaId]: box.ossUrl });
            }
          }
        } else if (box.type === 'MINIPROGRAM' && box.thumbMediaId) {
          if (box.thumbMediaId_ossUrl && typeof handleUpdatePreviewUrls === 'function') {
            handleUpdatePreviewUrls({ [box.thumbMediaId]: box.thumbMediaId_ossUrl });
          }
        }
      });
    }
  }, [inputBoxes]);

  // 监听momentBoxes变化，恢复朋友圈媒体状态
  useEffect(() => {
    if (momentBoxes && momentBoxes.length > 0) {
      console.log("momentBoxes变化，恢复媒体状态:", momentBoxes);
      
      momentBoxes.forEach(box => {
        if (box.type === 'MOMENT_IMAGE_GROUP' && box.imageGroup) {
          console.log(`恢复朋友圈图片集: ${box.id}`, box.imageGroup);
          updateImageGroup(box.id, box.imageGroup, 'MOMENTS');
          
          if (box.imageGroup.mediaIds && typeof handleUpdatePreviewUrls === 'function') {
            box.imageGroup.mediaIds.forEach(mediaId => {
              const ossUrl = box.imageGroup.ossUrls[mediaId];
              if (ossUrl) {
                handleUpdatePreviewUrls({ [mediaId]: ossUrl });
                console.log(`朋友圈图片集预览URL: ${mediaId} -> ${ossUrl}`);
              }
            });
          }
        } else if (['MOMENT_IMAGE', 'MOMENT_VIDEO'].includes(box.type) && box.mediaId) {
          // 单个朋友圈媒体文件
          console.log(`恢复朋友圈单个媒体: ${box.id}, 类型=${box.type}, mediaId=${box.mediaId}, ossUrl=${box.ossUrl}`);
          
          updateMediaId(box.id, box.mediaId, 'MOMENTS');
          
          const mediaType = box.type === 'MOMENT_IMAGE' ? 'image' : 'video';
          updateSpecificMediaId(box.id, box.mediaId, mediaType, 'MOMENTS');
          
          if (box.ossUrl) {
            updateOssUrl(box.id, box.ossUrl, 'MOMENTS');
            
            if (typeof handleUpdatePreviewUrls === 'function') {
              handleUpdatePreviewUrls({ [box.mediaId]: box.ossUrl });
              console.log(`朋友圈单个媒体预览URL: ${box.mediaId} -> ${box.ossUrl}`);
            }
          }
        }
      });
    }
  }, [momentBoxes]);

  // 简化的输入框操作函数
  const handleAddInputBox = (type) => {
    const newBox = {
      id: `msg-${type.toLowerCase()}-${Date.now()}`,
      type: type
    };

    if (type === 'TEXT') {
      newBox.content = '';
    } else if (type === 'LINK') {
      newBox.title = '';
      newBox.description = '';
      newBox.url = '';
    } else if (type === 'MINIPROGRAM') {
      newBox.title = '';
      newBox.appid = '';
      newBox.pagepath = '';
      newBox.thumbMediaId = '';
    } else if (type === 'IMAGE_GROUP') {
      newBox.imageGroup = {
        mediaIds: [],
        ossUrls: {}
      };
    } else if (type === 'VIDEO') {
      newBox.mediaId = '';
      newBox.ossUrl = '';
    }

    setInputBoxes([...inputBoxes, newBox]);
  };

  const handleRemoveInputBox = (id) => {
    setInputBoxes(inputBoxes.filter(box => box.id !== id));
    // 清理相关的媒体状态
    clearMedia(id, 'MESSAGE');
  };

  const handleUpdateInputBoxType = (id, type) => {
    const currentBox = inputBoxes.find(box => box.id === id);
    const oldType = currentBox?.type;

    setInputBoxes(inputBoxes.map(box =>
      box.id === id ? { ...box, type, mediaId: '', videoMediaId: '', fileMediaId: '' } : box
    ));

    // 清除表单错误和媒体状态
    const fieldsToReset = [];

    if (type !== 'TEXT') {
      fieldsToReset.push(['actionParams', 'content']);
    }

    if (type !== 'LINK') {
      fieldsToReset.push(
        ['actionParams', 'title'],
        ['actionParams', 'description'],
        ['actionParams', 'url']
      );
    }

    if (type !== 'MINIPROGRAM') {
      fieldsToReset.push(
        ['actionParams', 'title'],
        ['actionParams', 'appid'],
        ['actionParams', 'pagepath'],
        ['actionParams', 'thumbMediaId']
      );
    }

    fieldsToReset.push(
      ['actionParams', 'mediaId'],
      ['actionParams', 'videoMediaId'],
      ['actionParams', 'fileMediaId'],
      ['actionParams', 'picUrl']
    );

    fieldsToReset.forEach(field => {
      form.setFields([{
        name: field,
        errors: []
      }]);
    });

    if (oldType !== type) {
      clearMedia(id, 'MESSAGE');
    }
  };

  // 朋友圈操作函数
  const handleAddMomentBox = (type) => {
    const newBox = {
      id: `moment-${type.toLowerCase().replace('moment_', '')}-${Date.now()}`,
      type: type
    };

    if (type === 'MOMENT_TEXT') {
      newBox.content = '';
    } else if (type === 'MOMENT_LINK') {
      newBox.title = '';
      newBox.description = '';
      newBox.url = '';
    } else if (type === 'MOMENT_IMAGE_GROUP') {
      newBox.imageGroup = {
        mediaIds: [],
        ossUrls: {}
      };
    } else if (type === 'MOMENT_VIDEO') {
      newBox.mediaId = '';
      newBox.ossUrl = '';
    }

    setMomentBoxes([...momentBoxes, newBox]);
  };

  const handleRemoveMomentBox = (id) => {
    setMomentBoxes(momentBoxes.filter(box => box.id !== id));
    // 清理相关的媒体状态
    clearMedia(id, 'MOMENTS');
  };

  const handleUpdateMomentBoxType = (id, type) => {
    const currentBox = momentBoxes.find(box => box.id === id);
    const oldType = currentBox?.type;

    setMomentBoxes(momentBoxes.map(box =>
      box.id === id ? { ...box, type, mediaId: '' } : box
    ));

    // 清除不相关字段的错误
    const fieldsToReset = [];

    if (type !== 'MOMENT_TEXT') {
      fieldsToReset.push(['actionParams', 'content'], ['actionParams', 'momentText']);
    }

    if (type !== 'MOMENT_LINK') {
      fieldsToReset.push(
        ['actionParams', 'title'],
        ['actionParams', 'description'],
        ['actionParams', 'url']
      );
    }

    fieldsToReset.push(['actionParams', 'mediaId'], ['actionParams', 'picUrl']);

    fieldsToReset.forEach(field => {
      form.setFields([{
        name: field,
        errors: []
      }]);
    });

    if (oldType !== type) {
      clearMedia(id, 'MOMENTS');
    }
  };

  // 简化的步骤类型变更处理
  const handleStepTypeChange = (value) => {
    if (value === 'MOMENTS') {
      // 为朋友圈创建默认的文本和图片集附件
      setMomentBoxes([
        { 
          id: `moment-text-${Date.now()}`, 
          type: 'MOMENT_TEXT', 
          content: '' 
        }
      ]);
      // 清空消息附件
      setInputBoxes([]);
    } else if (value === 'MESSAGE') {
      // 为消息创建默认的文本附件
      setInputBoxes([
        { 
          id: `text-${Date.now()}`, 
          type: 'TEXT', 
          content: '' 
        }
      ]);
      // 清空朋友圈附件
      setMomentBoxes([]);
    } else {
      // 其他类型清空所有附件
      setInputBoxes([]);
      setMomentBoxes([]);
    }
  };

  // 简化的保存处理
  const handleSave = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      const stepData = buildStepData(values, currentStep, inputBoxes, momentBoxes, mediaState, isScheduled);
      
      if (stepData) {
        onSave(stepData);
        console.log("步骤保存完成，数据已传递给父组件");
      }
    } catch (error) {
      console.error('步骤验证失败:', error);
      message.error('请检查表单数据');
    }
  };

  // 渲染函数
  const renderDateTimePickers = () => {
    return <TimeSelector isScheduled={isScheduled} form={form} />;
  };

  const renderStepLevelSettings = () => {
    return (
      <TargetSelector 
        isScheduled={isScheduled} 
        tags={tags} 
        groups={groups} 
        form={form} 
      />
    );
  };

  // 为AttachmentManager创建的文件上传处理函数
  const handleAttachmentFileUpload = (options) => {
    console.log("AttachmentManager文件上传:", options);
    
    const { fieldKey, mediaType, stepType, file, onSuccess, onError, onProgress } = options;
    
    // 从fieldKey确定附件ID
    let attachmentId = fieldKey;
    let isThumb = false;
    
    // 处理小程序缩略图的特殊情况
    if (fieldKey.includes('_thumb')) {
      attachmentId = fieldKey.replace('_thumb', '');
      isThumb = true;
    }
    
    // 根据stepType选择正确的updateContent函数
    const updateContent = stepType === 'MESSAGE' ? handleUpdateInputBoxContent : handleUpdateMomentBoxContent;
    
    return handleFileUpload({
      file,
      fieldKey,
      mediaType,
      stepType,
      useTempMedia: true,
      onSuccess: (result) => {
        console.log("AttachmentManager上传成功:", result);
        
        if (isThumb) {
          // 小程序缩略图上传成功
          updateContent(attachmentId, 'thumbMediaId', result.mediaId);
          if (result.ossUrl) {
            updateContent(attachmentId, 'thumbMediaId_ossUrl', result.ossUrl);
          }
        } else {
          // 普通附件上传成功
          updateContent(attachmentId, 'mediaId', result.mediaId);
          if (result.ossUrl) {
            updateContent(attachmentId, 'ossUrl', result.ossUrl);
          }
        }
        
        // 更新预览URLs
        if (typeof handleUpdatePreviewUrls === 'function' && result.ossUrl) {
          handleUpdatePreviewUrls({ [result.mediaId]: result.ossUrl });
        }
        
        if (onSuccess) onSuccess(result);
      },
      onError: (error) => {
        console.error("AttachmentManager上传失败:", error);
        if (onError) onError(error);
      },
      onProgress
    });
  };

  // 为AttachmentManager创建的文件移除处理函数
  const handleAttachmentFileRemove = (fieldKey, stepType, mediaIdToRemove = null) => {
    console.log("AttachmentManager文件移除:", fieldKey, stepType, "要移除的mediaId:", mediaIdToRemove);
    
    // 从fieldKey确定附件ID
    let attachmentId = fieldKey;
    let isThumb = false;
    
    // 处理小程序缩略图的特殊情况
    if (fieldKey.includes('_thumb')) {
      attachmentId = fieldKey.replace('_thumb', '');
      isThumb = true;
    }
    
    // 根据stepType选择正确的updateContent函数和数据源
    const updateContent = stepType === 'MESSAGE' ? handleUpdateInputBoxContent : handleUpdateMomentBoxContent;
    const attachmentData = stepType === 'MESSAGE' ? inputBoxes : momentBoxes;
    
    // 找到当前附件
    const currentAttachment = attachmentData.find(att => att.id === attachmentId);
    
    if (isThumb) {
      // 清除小程序缩略图
      updateContent(attachmentId, 'thumbMediaId', '');
      updateContent(attachmentId, 'thumbMediaId_ossUrl', '');
    } else if (mediaIdToRemove && currentAttachment?.imageGroup) {
      // 特殊处理：从图片组中移除特定图片
      console.log("从图片组中移除图片:", mediaIdToRemove, "当前图片组:", currentAttachment.imageGroup);
      
      const newImageGroup = {
        mediaIds: currentAttachment.imageGroup.mediaIds.filter(id => id !== mediaIdToRemove),
        ossUrls: { ...currentAttachment.imageGroup.ossUrls }
      };
      
      // 删除对应的ossUrl
      delete newImageGroup.ossUrls[mediaIdToRemove];
      
      // 更新图片组数据
      updateContent(attachmentId, 'imageGroup', newImageGroup);
      
      // 清理媒体状态中的特定图片
      removeFromImageGroup(attachmentId, mediaIdToRemove, stepType);
    } else {
      // 清除普通附件
      updateContent(attachmentId, 'mediaId', '');
      updateContent(attachmentId, 'ossUrl', '');
    }
    
    // 调用原有的移除逻辑
    if (handleRemoveFile) {
      handleRemoveFile(fieldKey, stepType);
    }
  };

  // 渲染表单内容
  const getFormItems = () => {
    return (
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          actionParams: {
            content: ''
          }
        }}
        disabled={isTemplateActive}
        onValuesChange={(changedValues) => {
          if (changedValues.type) {
            handleStepTypeChange(changedValues.type);
          }
        }}
        size="middle"
        style={{ padding: '0 8px' }}
      >
        {/* 基本信息区域 */}
        <div style={{ marginBottom: '12px' }}>
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入名称' }]}
            style={{ marginBottom: '10px' }}
          >
            <Input placeholder="请输入名称" style={{ width: '50%' }} />
          </Form.Item>

          <Form.Item
            name="stepCode"
            label="步骤编码"
            rules={[{ required: true, message: '请输入步骤编码' }]}
            hidden={true}
            style={{ marginBottom: '10px' }}
          >
            <Input placeholder="请输入步骤编码" />
          </Form.Item>

          {renderDateTimePickers()}

          <Form.Item
            name="type"
            label="触达形式"
            rules={[{ required: true, message: '请选择触达形式' }]}
            style={{ marginBottom: '10px' }}
          >
            <Radio.Group style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              {stepTypes.map(type => {
                const Icon = require('@ant-design/icons')[type.icon];
                return (
                  <Radio
                    key={type.value}
                    value={type.value}
                    disabled={type.disabled}
                    style={{
                      height: 'auto',
                      margin: '3px 0',
                      padding: '8px 0',
                      display: 'flex',
                      alignItems: 'flex-start'
                    }}
                  >
                    <span style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        {Icon && <Icon style={{ marginRight: '8px', fontSize: '16px' }} />}
                        {type.label}
                      </span>
                      {type.description && (
                        <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px', lineHeight: '1.3', paddingLeft: '24px' }}>
                          {type.description}
                        </Text>
                      )}
                    </span>
                  </Radio>
                );
              })}
            </Radio.Group>
          </Form.Item>

          {renderStepLevelSettings()}
        </div>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
        >
          {({ getFieldValue }) => {
            const stepType = getFieldValue('type');

            if (stepType === 'MESSAGE') {
              return (
                <AttachmentManager
                  attachments={inputBoxes}
                  onUpdateContent={handleUpdateInputBoxContent}
                  onUpdateType={handleUpdateInputBoxType}
                  onAddAttachment={handleAddInputBox}
                  onRemoveAttachment={handleRemoveInputBox}
                  mediaState={mediaState}
                  previewUrls={previewUrls}
                  uploading={uploading}
                  uploadProgress={uploadProgress}
                  isTemplateActive={isTemplateActive}
                  handleFileUpload={handleAttachmentFileUpload}
                  handleRemoveFile={handleAttachmentFileRemove}
                  stepType="MESSAGE"
                  validateUrl={validateUrl}
                  handleUpdatePreviewUrls={handleUpdatePreviewUrls}
                />
              );
            }

            if (stepType === 'MOMENTS') {
              return (
                <AttachmentManager
                  attachments={momentBoxes}
                  onUpdateContent={handleUpdateMomentBoxContent}
                  onUpdateType={handleUpdateMomentBoxType}
                  onAddAttachment={handleAddMomentBox}
                  onRemoveAttachment={handleRemoveMomentBox}
                  mediaState={mediaState}
                  previewUrls={previewUrls}
                  uploading={uploading}
                  uploadProgress={uploadProgress}
                  isTemplateActive={isTemplateActive}
                  handleFileUpload={handleAttachmentFileUpload}
                  handleRemoveFile={handleAttachmentFileRemove}
                  stepType="MOMENTS"
                  validateUrl={validateUrl}
                  handleUpdatePreviewUrls={handleUpdatePreviewUrls}
                />
              );
            }

            if (stepType === 'TAG') {
              return <TagEditor tags={tags} isTemplateActive={isTemplateActive} />;
            }

            return null;
          }}
        </Form.Item>
      </Form>
    );
  };

  return (
    <Drawer
      open={visible}
      title={title}
      width="50%"
      onClose={onCancel}
      placement="right"
      bodyStyle={{ padding: '16px 8px' }}
      contentWrapperStyle={{ maxWidth: '800px' }}
      extra={
        <Button
          type="primary"
          onClick={handleSave}
          disabled={isTemplateActive}
        >
          保存
        </Button>
      }
    >
      {getFormItems()}
    </Drawer>
  );
};

export default StepModal;